import { useState } from "react";
import { Check, Copy, Link2, MessageCircle, RefreshCw, UserCog, UserMinus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useLoad, useMember } from "@/lib/api";
import { Empty, ErrorBox, PageHeading } from "@/components/common";

function JoinCode() {
  const me = useMember();
  const [code, setCode] = useState(me.join_code);
  const copy = () => { navigator.clipboard?.writeText(code); toast.success("Clinic code copied"); };
  const link = `${window.location.origin}/join/${code}`;
  const copyLink = () => { navigator.clipboard?.writeText(link); toast.success("Invite link copied"); };
  const rotate = async () => {
    if (!window.confirm("Create a new code? The old code will stop working for new requests.")) return;
    try { const r = await api.post("/clinic/join-code"); setCode(r.data.join_code); toast.success("New clinic code created"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="join-code-card" data-testid="join-code-card">
      <div><p className="eyebrow">CLINIC CODE</p><b data-testid="join-code-value">{code}</b><small>Share the invite link with your staff. They register, pick their role and you approve them below. They can also type this code.</small>
        <div className="invite-row"><code data-testid="invite-link-value">{link}</code>
          <button className="secondary" onClick={copyLink} data-testid="copy-invite-link-button"><Link2 size={14} /> Copy link</button>
          <a className="wa-btn" href={`https://wa.me/?text=${encodeURIComponent(`Join ${me.clinic?.name || "our clinic"} on DentaflowAI: ${link}`)}`} target="_blank" rel="noreferrer" data-testid="share-invite-whatsapp"><MessageCircle size={14} /> Share on WhatsApp</a>
        </div>
      </div>
      <div className="row-actions"><button className="secondary" onClick={copy} data-testid="copy-join-code-button"><Copy size={14} /> Copy</button><button className="icon-btn" onClick={rotate} title="New code" data-testid="rotate-join-code-button"><RefreshCw size={15} /></button></div>
    </section>
  );
}

function Requests({ rows, roles, onChanged }) {
  const [choice, setChoice] = useState({});
  const act = async (u, action) => {
    try {
      if (action === "approve") await api.post(`/users/${u.user_id}/approve`, { role: choice[u.user_id] || u.requested_role || "dentist" });
      else await api.post(`/users/${u.user_id}/reject`);
      toast.success(action === "approve" ? `${u.name || u.email} approved` : "Request declined"); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows.length) return null;
  return (
    <section className="requests-card" data-testid="join-requests">
      <p className="eyebrow">WAITING FOR YOUR APPROVAL ({rows.length})</p>
      {rows.map((u) => (
        <div className="request-row" key={u.user_id} data-testid={`join-request-${u.email}`}>
          <div className="person"><div className="avatar soft">{(u.name || u.email || "?").slice(0, 2).toUpperCase()}</div><span><b>{u.name || u.email}</b><small>{u.email} · asked {u.requested_at?.slice(0, 10)}</small></span></div>
          <select className="status-select" value={choice[u.user_id] || u.requested_role || "dentist"} onChange={(e) => setChoice({ ...choice, [u.user_id]: e.target.value })} data-testid={`request-role-select-${u.email}`}>{roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select>
          <div className="row-actions"><button className="primary" onClick={() => act(u, "approve")} data-testid={`approve-request-${u.email}`}><Check size={15} /> Approve</button><button className="icon-btn" onClick={() => act(u, "reject")} title="Decline" data-testid={`reject-request-${u.email}`}><X size={15} /></button></div>
        </div>
      ))}
    </section>
  );
}

export default function Team() {
  const me = useMember();
  const { data, error, reload } = useLoad("/users", { roles: [], users: [] });
  const active = data.users.filter((u) => u.status === "active");
  const change = async (u, role) => {
    try { await api.patch(`/users/${u.user_id}/role`, { role }); toast.success("Role updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email} from the clinic? They will lose access immediately.`)) return;
    try { await api.post(`/users/${u.user_id}/reject`); toast.success("Team member removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="content">
      <PageHeading eyebrow={`TEAM & ACCESS · ${me.clinic?.name?.toUpperCase() || ""}`} title="The right access," accent="for each role." sub="Only people you approve can see this clinic's records." />
      <ErrorBox error={error} testid="team-error" />
      <JoinCode />
      <Requests rows={data.users.filter((u) => u.status === "pending")} roles={data.roles} onChanged={reload} />
      <section className="table-card">
        <div className="table-head team-grid"><span>Team member</span><span>Email</span><span>Joined</span><span>Role</span></div>
        {active.length ? active.map((u) => (
          <div className="table-row team-grid" key={u.user_id} data-testid={`team-row-${u.email}`}>
            <div className="person"><div className="avatar soft">{(u.name || u.email || "?").slice(0, 2).toUpperCase()}</div><b>{u.name || "—"}{u.user_id === me.user_id && " (you)"}</b></div>
            <span>{u.email}</span><span>{(u.joined_at || u.created_at || "").slice(0, 10) || "—"}</span>
            <span className="row-actions">
              <select className={`status-select ${u.role}`} value={u.role} onChange={(e) => change(u, e.target.value)} data-testid={`team-role-select-${u.email}`}>{data.roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select>
              {u.user_id !== me.user_id && <button className="icon-btn" onClick={() => remove(u)} title="Remove from clinic" data-testid={`remove-member-${u.email}`}><UserMinus size={15} /></button>}
            </span>
          </div>
        )) : <Empty icon={UserCog} title="No team members yet" text="Share the clinic code with your staff." testid="team-empty" />}
      </section>
      <div className="role-guide" data-testid="role-guide">
        <div><b>Clinic administrator</b><span>Everything, including team approval, settings, deleting patients and voiding entries.</span></div>
        <div><b>Dentist</b><span>Patients, clinical notes, treatments, payments, lab cases and voice entry.</span></div>
        <div><b>Accountant</b><span>Patients, ledgers, payments, expenses, lab payments and closing the day. No clinical notes.</span></div>
        <div><b>Receptionist</b><span>Patients, payments, appointments, expenses and lab payments. No clinical notes.</span></div>
      </div>
    </div>
  );
}
